import React from 'react';
import {FormattedMessage} from 'react-intl';

import {IPatientRecurrence} from 'redux/intervention/interfaces';

interface IProgramProgressTextProps {
  modulesCompleted: number
  modulesTotal: number
  recurrence?: IPatientRecurrence | null
  daysElapsed?: number | null
  pendingCount?: number
  className?: string
}

const RecurrenceText: React.FC<{recurrence: IPatientRecurrence; daysElapsed?: number | null}> = ({
  recurrence,
  daysElapsed
}) => {
  const values = {done: recurrence.completed, generated: recurrence.generated, day: daysElapsed};

  if (daysElapsed == null) {
    return (
      <FormattedMessage
        id="gritx.progress.recurrence"
        defaultMessage="{done} of {generated} check-ins done"
        values={values}
      />
    );
  }

  return (
    <FormattedMessage
      id="gritx.progress.dayRecurrence"
      defaultMessage="Day {day} · {done} of {generated} check-ins done"
      values={values}
    />
  );
};

// Progress wording per program shape: a finite program shows "x / y modules"; a recurring-only
// program shows the day and check-in adherence instead (it has no finish line); a mix shows both.
// "N activities pending" is appended to the first line.
export const ProgramProgressText: React.FC<IProgramProgressTextProps> = ({
  modulesCompleted,
  modulesTotal,
  recurrence,
  daysElapsed,
  pendingCount = 0,
  className
}) => {
  const hasModules = modulesTotal > 0;

  let first: React.ReactNode = null;
  let second: React.ReactNode = null;

  if (hasModules) {
    first = (
      <>
        {modulesCompleted}&nbsp;/&nbsp;{modulesTotal}{' '}
        <FormattedMessage id="gritx.interventions.modules" defaultMessage="modules"/>
      </>
    );
    second = recurrence ? <RecurrenceText recurrence={recurrence} daysElapsed={daysElapsed}/> : null;
  } else if (recurrence) {
    first = <RecurrenceText recurrence={recurrence} daysElapsed={daysElapsed}/>;
  } else if (daysElapsed != null) {
    first = <FormattedMessage id="gritx.progress.day" defaultMessage="Day {day}" values={{day: daysElapsed}}/>;
  }

  const pending = pendingCount > 0 && (
    <>
      {first && ' · '}
      <FormattedMessage
        id="gritx.dashboard.activitiesPending"
        defaultMessage="{count, plural, one {# activity pending} other {# activities pending}}"
        values={{count: pendingCount}}
      />
    </>
  );

  if (!first && !pending) {
    return null;
  }

  return (
    <>
      <div className={className}>{first}{pending}</div>
      {second && <div className={className}>{second}</div>}
    </>
  );
};
